/**
 * ServiceProviderCard Component
 * Display information about waste management service providers
 */

import type { ServiceProvider } from '../../types/county';

interface ServiceProviderCardProps {
  provider: ServiceProvider;
}

export function ServiceProviderCard({ provider }: ServiceProviderCardProps) {
  return (
    <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <h3 className="font-semibold text-lg text-neutral-900">{provider.name}</h3>
        {provider.countyApproved ? (
          <span className="inline-block px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
            County Approved
          </span>
        ) : (
          <span className="inline-block px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
            Pending Verification
          </span>
        )}
      </div>

      {/* Description */}
      {provider.description && (
        <p className="text-sm text-neutral-600 mb-4">
          {provider.description}
        </p>
      )}

      {/* Services */}
      <div className="mb-4">
        <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">
          Services Offered
        </div>
        <div className="flex flex-wrap gap-2">
          {provider.services.map((service, idx) => (
            <span
              key={idx}
              className="inline-block px-3 py-1 text-sm bg-primary-50 text-primary-700 border border-primary-200 rounded-lg"
            >
              {service}
            </span>
          ))}
        </div>
      </div>

      {/* Service Area */}
      <div className="mb-4">
        <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">
          Service Area
        </div>
        <div className="text-sm text-neutral-700">
          {provider.serviceArea.join(', ')}
        </div>
      </div>

      {/* Contact Information */}
      <div className="pt-4 border-t border-neutral-200">
        <div className="flex flex-wrap gap-4 text-sm">
          {provider.phone && (
            <a
              href={`tel:${provider.phone}`}
              className="text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              aria-label={`Call ${provider.name}`}
            >
              <span>📞</span>
              <span>{provider.phone}</span>
            </a>
          )}
          {provider.email && (
            <a
              href={`mailto:${provider.email}`}
              className="text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              aria-label={`Email ${provider.name}`}
            >
              <span>✉️</span>
              <span>Email</span>
            </a>
          )}
          {provider.website && (
            <a
              href={provider.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              aria-label={`Visit ${provider.name} website`}
            >
              <span>🌐</span>
              <span>Website</span>
              <span>→</span>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
